import { template as template_f67feaff7d754919b26b32b07bedaf75 } from "@ember/template-compiler";
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { isEmpty } from '@ember/utils';
import { waitFor } from '@ember/test-waiters';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import BsDropdown from 'ember-bootstrap/components/bs-dropdown';
import { task, timeout } from 'ember-concurrency';
import { eq, or } from 'ember-truth-helpers';
import { t } from 'ember-intl';
import { isTesting, macroCondition } from '@embroider/macros';
import FaIcon from '@fortawesome/ember-fontawesome/components/fa-icon';
import * as R from 'ramda';
import { onResize } from 'eflex/modifiers';
export const getRemainingAfterMax = R.curry((maxSum, inputMap)=>{
    const pairs = Array.from(inputMap.entries());
    const cumulativeSums = R.tail(R.scan((acc, [_, value])=>acc + value, 0, pairs));
    const idx = R.findIndex((sum)=>sum > maxSum, cumulativeSums);
    const splitIdx = idx === -1 ? pairs.length : idx;
    const remainingPairs = R.slice(splitIdx, Infinity, pairs);
    return new Map(remainingPairs);
});
class Tab extends Component {
    get tabId() {
        return `tab-${this.args.href}`;
    }
    get href() {
        return `#${this.tabId}`;
    }
    get hidden() {
        return this.args.hiddenTabs?.includes(this.tabId);
    }
    get isActive() {
        return !isEmpty(this.args.href) && this.args.href === this.args.activeTab;
    }
    get isMenuItem() {
        return this.hidden && this.args.menu;
    }
    get isListItem() {
        return !this.hidden && !this.args.menu;
    }
    onShown = ({ target })=>{
        this.args.tabBecameActive(target.hash.replace('#tab-', ''));
    };
    static{
        template_f67feaff7d754919b26b32b07bedaf75(`
    {{#if this.isListItem}}
      <li
        id={{this.tabId}}
        class="eflex-tab nav-item text-nowrap"
        {{on 'shown.bs.tab' this.onShown}}
        ...attributes
      >
        <a
          href={{this.href}}
          data-bs-toggle={{unless @disabled "tab"}}
          class="nav-link {{if this.isActive "active"}} {{if @disabled "disabled"}}"
        >
          {{yield}}
        </a>
      </li>
    {{/if}}
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
class MenuTab extends Tab {
    static{
        template_f67feaff7d754919b26b32b07bedaf75(`
    {{#if this.isMenuItem}}
      <@menu.item>
        <div
          class="dropdown-item"
          {{on 'shown.bs.tab' this.onShown}}
        >
          <a
            href={{this.href}}
            data-bs-toggle={{unless @disabled "tab"}}
            class="nav-link {{if this.isActive "active"}} {{if @disabled "disabled"}}"
          >
            {{yield}}
          </a>
        </div>
      </@menu.item>
    {{/if}}
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
const Navigation = template_f67feaff7d754919b26b32b07bedaf75(`
<ul
  class="nav flex-nowrap eflex-tabs px-3 overflow-hidden"
  {{onResize @handleResize}}
  {{didInsert @onDidInsert}}
  ...attributes
>
  {{yield
    (hash
      tab=(component
        Tab
        tabBecameActive=@tabBecameActive
        activeTab=@activeTab
        hiddenTabs=@hiddenTabs
      )
    )
  }}

  {{#if (or @hiddenTabs.length @measureMore)}}
    <BsDropdown
      id={{@moreTabId}}
      class="tabs-dropdown-menu eflex-tab nav-item pe-3 z-top-menu"
      @direction="bottom"
    as |dd|>
      <dd.toggle
        class="tabs-dropdown d-block h-100"
      >
        <FaIcon @icon="ellipsis-vertical" @prefix="fas" class="fa-fw"/> {{t "tabs.more"}}
      </dd.toggle>
      <dd.menu class="dropdown-menu fs-3 me-4" as |menu|>
        {{yield
          (hash
            tab=(component
              MenuTab
              tabBecameActive=@tabBecameActive
              activeTab=@activeTab
              hiddenTabs=@hiddenTabs
              menu=menu
            )
          )
        }}
      </dd.menu>
    </BsDropdown>
  {{/if}}
</ul>
`, {
    eval () {
        return eval(arguments[0]);
    }
});
const Pane = template_f67feaff7d754919b26b32b07bedaf75(`
{{#let (eq @activeTab @id) as |isActive|}}
  <div
    id="pane-{{@id}}"
    class="{{if isActive "active"}} tab-pane"
    ...attributes
  >
    {{#if isActive}}
      {{yield}}
    {{/if}}
  </div>
{{/let}}
`, {
    eval () {
        return eval(arguments[0]);
    }
});
const Panes = template_f67feaff7d754919b26b32b07bedaf75(`
<div class="tab-content flex-fill overflow-hidden" ...attributes>
  {{yield (hash pane=(component Pane activeTab=@activeTab))}}
</div>
`, {
    eval () {
        return eval(arguments[0]);
    }
});
export default class NoRouteFitTabs extends Component {
    @tracked
    navElement;
    @tracked
    navWidth;
    @tracked
    hiddenTabs = [];
    @tracked
    tabWidths;
    @tracked
    moreWidth = 0;
    @tracked
    measureMore = true;
    moreTabId = 'tab-more-dropdown';
    onDidInsert = task(waitFor(async (navElement)=>{
        const tabsElements = Array.from(navElement.querySelectorAll(`.eflex-tab:not(#${this.moreTabId})`));
        const tabWidths = new Map();
        tabsElements.forEach((el)=>{
            if (!isEmpty(el)) {
                tabWidths.set(el.id, el.offsetWidth ?? 0);
            }
        });
        this.tabWidths = tabWidths;
        this.moreWidth = navElement.querySelector(`#${this.moreTabId}`).offsetWidth;
        this.measureMore = false;
        this.navElement = navElement;
        await this.handleResize.perform(navElement, false);
    }));
    handleResize = task({
        drop: true
    }, waitFor(async (navElement, delay = true)=>{
        if (macroCondition(isTesting())) {
            return;
        }
        if (delay) {
            await timeout(300);
        }
        const navWidth = navElement.offsetWidth;
        let maxWidth = navWidth;
        let hiddenTabMap = getRemainingAfterMax(maxWidth, this.tabWidths);
        let toHide = hiddenTabMap.keys()?.toArray();
        if (!isEmpty(toHide)) {
            maxWidth = maxWidth - this.moreWidth;
            hiddenTabMap = getRemainingAfterMax(maxWidth, this.tabWidths);
            toHide = hiddenTabMap.keys().toArray();
            const activeTabId = `tab-${this.args.activeTab}`;
            if (toHide.includes(activeTabId)) {
                const activeWidth = this.tabWidths.get(activeTabId);
                maxWidth = maxWidth - activeWidth;
                hiddenTabMap = getRemainingAfterMax(maxWidth, this.tabWidths);
                toHide = hiddenTabMap.keys().toArray();
                const activeIndex = toHide.indexOf(activeTabId);
                toHide.splice(activeIndex, 1);
            }
        }
        this.navWidth = navWidth;
        this.hiddenTabs = toHide;
    }));
    tabBecameActive = task(waitFor(async (tab)=>{
        this.args.tabBecameActive(tab);
        await this.handleResize.perform(this.navElement, false);
    }));
    static{
        template_f67feaff7d754919b26b32b07bedaf75(`
    {{yield
      (hash
        navigation=(component
          Navigation
          tabBecameActive=this.tabBecameActive.perform
          activeTab=@activeTab
          hiddenTabs=this.hiddenTabs
          measureMore=this.measureMore
          moreTabId=this.moreTabId
          onDidInsert=this.onDidInsert.perform
          handleResize=this.handleResize.perform
        )
        panes=(component Panes activeTab=@activeTab)
      )
    }}
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
